import moment from 'moment';

import { computed, observable, action } from 'mobx';

import { WebGivingPage } from '../index';
import { FormField } from '../../components/form-field';
import { ValidationState, validateFields } from '../../validation/view-model-validator';
import {
	EndType,
	FrequencyCode,
	PaymentLabel,
	WebGivingMerchant,
	ModelMetadata,
	BrandingPackage,
	FundVisibility,
	CustomFieldType,
} from '../../webgiving-generated';
import { ReferenceFieldsViewModel } from '../../components/reference-fields/reference-fields-vew-model';
import { IFormControlSplitButtonOptions } from '../../components/split-button';
import {
	combineValidators,
	createRulesFromMetadata,
	FieldValidationFailure,
	RuleType,
	ValidatorFunc,
	createValidatorFunc,
	fieldValidationSuccess,
	valueInRange,
	alwaysValid,
} from '../../validation/validation-rules';
import { Formatter } from '../../../Shared/utils/formatter';

import giveIcon from '../../assets/svg/gift.svg';
import recurringIcon from '../../assets/svg/recurring.svg';
import { IWebGivingPaymentState, GiftType } from '../../webgiving-payment-state';
import { frequencyLabelSentenceCase } from '../../utils/recurring-utils';
import { getNextFirstOrFifteenthAfter, isAfterToday } from '../../utils/date-utils';
import { WebGivingPageViewModelBase } from '../page-view-model-base';
import { OptionalDate } from '../../components/date-picker/date-picker';
import { GivingType } from './page-give';
import { LineItemsViewModel } from '../../components/line-items/line-items-view-model';
import { getMinMaxPaymentAmounts, isTotalOverMaxLimit } from '../../utils/helper-utils';

export class PageGiveViewModel extends WebGivingPageViewModelBase {
	readonly type: WebGivingPage.Give = WebGivingPage.Give;
	referenceFieldsViewModel: ReferenceFieldsViewModel;

	@observable
	lineItemsViewModel: LineItemsViewModel;

	@observable
	hasLineItems: boolean;

	@observable
	validationState?: ValidationState;

	paymentLabel: PaymentLabel;

	amount: FormField<number>;

	giftType: FormField<GiftType>;

	giftTypeOptions: IFormControlSplitButtonOptions<GiftType>[];

	frequency: FormField<FrequencyCode>;

	frequencyOptions = [
		{ value: FrequencyCode.Weekly, label: frequencyLabelSentenceCase(FrequencyCode.Weekly) },
		{ value: FrequencyCode.Fortnightly, label: frequencyLabelSentenceCase(FrequencyCode.Fortnightly) },
		{ value: FrequencyCode.Monthly, label: frequencyLabelSentenceCase(FrequencyCode.Monthly) },
		{ value: FrequencyCode.FirstAndFifteenth, label: frequencyLabelSentenceCase(FrequencyCode.FirstAndFifteenth) },
	];

	frequencyOptionsForTesting = [
		{ value: FrequencyCode.OneMinute, label: frequencyLabelSentenceCase(FrequencyCode.OneMinute) },
		{ value: FrequencyCode.FiveMinutes, label: frequencyLabelSentenceCase(FrequencyCode.FiveMinutes) },
		{ value: FrequencyCode.FourHours, label: frequencyLabelSentenceCase(FrequencyCode.FourHours) },
	];

	startDate: FormField<OptionalDate>;

	endType: FormField<EndType>;

	endTypeOptions: IFormControlSplitButtonOptions<EndType>[];

	endDate: FormField<OptionalDate>;

	endOccurrences: FormField<number | null>;

	givingType: FormField<GivingType>;

	@observable
	givingTypeOptions: IFormControlSplitButtonOptions<GivingType>[];

	oneTimeGiftDate: FormField<OptionalDate>;

	handleStartDateChange = action((newStartDate: OptionalDate) => {
		this.clearEndDateIfItIsBeforeStartDate(newStartDate);
	});

	@observable
	recurringSelectorIsVisible: boolean;

	fundVisibility: FundVisibility;

	@computed
	get isRecurring(): boolean {
		return this.giftType.value === GiftType.Recurring;
	}

	@computed
	get showsOnlyAmount(): boolean {
		if (this.isRecurringVisible) {
			return false;
		}

		if (this.isReferenceFieldsVisible) {
			return false;
		}

		if (!this.isRecurring && this.merchantInfo && this.merchantInfo.OneTimeFutureGiftEnabled) {
			return false;
		}

		return true;
	}

	@computed
	get isRecurringVisible() {
		if (this.recurringSelectorIsVisible) {
			return true;
		}

		if (this.isRecurring) {
			return true;
		}

		return false;
	}

	@computed
	get isReferenceFieldsVisible() {
		const allCustomFields = this.referenceFieldsViewModel.customFields;
		const hasReferenceFields = allCustomFields.some((field) => field.Type !== CustomFieldType.Fund);
		if (hasReferenceFields) {
			return true;
		}

		const fundExistsInReferenceFields = allCustomFields.some((field) => field.Type === CustomFieldType.Fund);
		if (fundExistsInReferenceFields) {
			if (this.fundVisibility !== FundVisibility.Hide) {
				return true;
			}
		}

		return false;
	}

	readonly minStartDate: Date = new Date();

	readonly maxDate: Date = new Date('2099-12-31');

	@computed
	get minEndDate(): Date {
		const start = this.startDate.value;
		const minDate = start ? moment(start).add(1, 'days') : moment();
		return minDate.toDate();
	}

	@computed
	get hasCryptoGiving(): boolean {
		return !!this.merchantInfo.MerchantCryptoLink;
	}

	@computed
	get hasStockGiving(): boolean {
		return !!this.merchantInfo.MerchantStockGivingLink;
	}

	@computed
	get isSplitGivingAllowed(): boolean {
		return !this.paymentState.amountIsLocked && this.merchantInfo.SplitGivingEnabled;
	}

	constructor(
		public paymentState: IWebGivingPaymentState,
		public merchantInfo: WebGivingMerchant,
		public brandingPackage: BrandingPackage | null
	) {
		super();

		this.paymentLabel = this.merchantInfo.PaymentLabel;

		let minPaymentAmount = this.merchantInfo.PaymentMin ? this.merchantInfo.PaymentMin : 1;
		let maxPaymentAmount: number;

		const result = getMinMaxPaymentAmounts(merchantInfo);
		minPaymentAmount = result.minPaymentAmount;
		maxPaymentAmount = result.maxPaymentAmount;

		this.amount = new FormField<number>(
			paymentState.amount,
			this.createAmountValidator(minPaymentAmount, maxPaymentAmount),
			{
				minPaymentAmount: Formatter.formatNumberForDisplay(minPaymentAmount, 2),
				maxPaymentAmount: Formatter.formatNumberForDisplay(maxPaymentAmount, 2),
			}
		);

		if (paymentState.amountIsLocked) {
			this.amount.lockAmount();
		}

		this.giftType = FormField.Unvalidated(paymentState.giftType);
		this.giftTypeOptions = [
			{ value: GiftType.OneTime, label: `${this.paymentLabel.VerbSentenceCase} one time`, icon: giveIcon },
			{ value: GiftType.Recurring, label: 'Set up recurring', icon: recurringIcon },
		];

		const today = moment().toDate();

		const recurringInfo =
			paymentState.giftType === GiftType.Recurring
				? paymentState.recurringInfo
				: {
						frequency: FrequencyCode.Monthly,
						startDate: today,
						endType: EndType.Never,
						endDate: null,
						endOccurrences: null,
				  };
		recurringInfo.startDate = recurringInfo.startDate || today;
		recurringInfo.endDate = recurringInfo.endDate || null;
		recurringInfo.endOccurrences = recurringInfo.endOccurrences || null;

		if (merchantInfo.HasQuarterlyRecurringGivingEnabled) {
			this.frequencyOptions.push({
				value: FrequencyCode.Quarterly,
				label: frequencyLabelSentenceCase(FrequencyCode.Quarterly),
			});
		}

		if (merchantInfo.HasSemiYearlyRecurringGivingEnabled) {
			this.frequencyOptions.push({
				value: FrequencyCode.SemiYearly,
				label: frequencyLabelSentenceCase(FrequencyCode.SemiYearly),
			});
		}

		if (merchantInfo.HasYearlyRecurringGivingEnabled) {
			this.frequencyOptions.push({
				value: FrequencyCode.Yearly,
				label: frequencyLabelSentenceCase(FrequencyCode.Yearly),
			});
		}

		if (NewFeatures.WebGiving_AddTestOnlyRecurringPaymentFrequencies) {
			if (NewFeatures.TesterCommandsEnabled) {
				this.frequencyOptions.push(...this.frequencyOptionsForTesting);
			}
		}

		this.frequency = new FormField<FrequencyCode>(
			recurringInfo.frequency,
			valueInRange(
				this.frequencyOptions.map((x) => x.value),
				'Please specify the frequency'
			)
		);

		this.startDate = new FormField<OptionalDate>(recurringInfo.startDate, this.createStartDateValidator());
		this.endType = FormField.Unvalidated(recurringInfo.endType);
		this.endTypeOptions = [
			{ value: EndType.Never, label: 'When I cancel it' },
			{ value: EndType.Occurrences, label: `After number of ${this.paymentLabel.NounPluralLowerCase}` },
			{ value: EndType.Date, label: 'On a specific date' },
		];
		this.endDate = new FormField<OptionalDate>(recurringInfo.endDate, this.createEndDateValidator());

		this.givingType = new FormField<GivingType>(GivingType.CardorACH, alwaysValid());

		this.oneTimeGiftDate = new FormField<OptionalDate>(
			paymentState.giftType === GiftType.OneTime && paymentState.giftDate ? paymentState.giftDate : new Date(),
			this.createOneTimeGiftDateValidator()
		);

		this.updateGivingTypeOptions(isAfterToday(this.oneTimeGiftDate.value ? this.oneTimeGiftDate.value : new Date()));

		this.recurringSelectorIsVisible =
			paymentState.RecurringSelectorIsVisible === null || paymentState.RecurringSelectorIsVisible === undefined
				? true
				: paymentState.RecurringSelectorIsVisible;

		this.endOccurrences = new FormField<number | null>(
			recurringInfo.endOccurrences,
			combineValidators(
				createRulesFromMetadata<number | null>(ModelMetadata.ConfirmPaymentRecurringDetails.EndOccurrences),
				this.createEndOccurrencesValidator()
			)
		);
		this.referenceFieldsViewModel = new ReferenceFieldsViewModel(
			this.merchantInfo.CustomFields,
			paymentState.referenceFieldValues
		);

		if (this.isSplitGivingAllowed) {
			this.hasLineItems = !!paymentState.LineItems.length;
			this.lineItemsViewModel = new LineItemsViewModel({
				customFields: this.merchantInfo.CustomFields,
				lineItems: paymentState.LineItems,
				minPaymentAmount,
				maxPaymentAmount,
				onUpdateCallback: (items, totalAmount) => {
					if (items.length > 0) {
						this.referenceFieldsViewModel.updateDefaultFundValue(items[0].FundKey);
					}

					this.hasLineItems = !!items.length;

					if (!this.hasLineItems) {
						this.amount.unLockAmount();
						return;
					}

					this.amount.lockAmount();
					this.amount.updateValue(totalAmount);

					if (!this.amount.hasValidationError) {
						this.amount.validateEveryTimeCauseImASpecialCase();
					}
				},
			});
		}

		this.fundVisibility = paymentState.fundVisibility;

		this.oneTimeGiftDate.updateSubject.register((fm) => this.updateGivingTypeOptions(isAfterToday(fm.value ? fm.value : new Date())));

		this.giftType.updateSubject.register(() => {
			this.setFrequencyIfMissing();
			this.updateGivingTypeOptions(isAfterToday(this.oneTimeGiftDate.value ? this.oneTimeGiftDate.value : new Date()));
		});

		this.amount.updateSubject.register((amount) => {
			if (amount && amount.hasValidationError) {
				// Revalidate to clear the error message
				amount.validateEveryTimeCauseImASpecialCase();
			}
		});
	}

	@action
	updateGivingTypeOptions = (isFutureGift?: boolean) => {
		const includeApplePayInLabel = this.merchantInfo.SupportsApplePayRecurring
			? this.merchantInfo.SupportsApplePay
			: !isFutureGift && this.merchantInfo.SupportsApplePay && this.giftType.value === GiftType.OneTime;
		this.givingTypeOptions = [
			{ value: GivingType.CardorACH, label: includeApplePayInLabel ? 'Card / ACH / Apple Pay' : 'Card / ACH' },
		]

		if (this.hasCryptoGiving) {
			this.givingTypeOptions.push({ value: GivingType.Cryptocurrency, label: `Cryptocurrency` });
		}

		if (this.hasStockGiving) {
			this.givingTypeOptions.push({ value: GivingType.Stock, label: 'Stocks' });
		}
	}

	handleFrequencyChange = (x: any) => {
		if (x === FrequencyCode.FirstAndFifteenth) {
			this.setStartDateToNextFirstOrFifteenth();
		}
	};

	// if we ever navigate away from 'recurring' leaving frequency unselected
	// when we come back it should be selected to the default: Monthly.
	setFrequencyIfMissing = () => {
		if (this.frequency.value === FrequencyCode.Other && !this.isRecurring) {
			this.frequency.updateValue(FrequencyCode.Monthly);
		}
	};

	getFilteredReferenceFieldsViewModel() {
		const filteredReferenceFieldsViewModel = { ...this.referenceFieldsViewModel };
		if (this.hasLineItems) {
			for (let key in this.referenceFieldsViewModel) {
				const field: any = this.referenceFieldsViewModel[key];
				if (field instanceof FormField) {
					const customField = this.merchantInfo.CustomFields.find(customField => customField.Key === key);
					if (customField && customField.Type === CustomFieldType.Fund) {
						delete filteredReferenceFieldsViewModel[key];
					}
				}
			}
		}
		return filteredReferenceFieldsViewModel;
	}

	validate(): ValidationState {
		if (this.isSplitGivingAllowed) {
			const filteredReferenceFieldsViewModel = this.getFilteredReferenceFieldsViewModel();
			return validateFields(this, 'Please enter valid details', {
				...filteredReferenceFieldsViewModel,
				...this.lineItemsViewModel.lineItemsFields
			});
		} else {
			return validateFields(this, 'Please enter valid details', this.referenceFieldsViewModel);
		}
	}

	private createAmountValidator = (minPaymentAmount: number, maxPaymentAmount: number): ValidatorFunc<number> => {
		return createValidatorFunc((amount: number) => {
			if (this.hasLineItems) {
				const isOverMaxLimit = isTotalOverMaxLimit(amount, maxPaymentAmount);
				if (isOverMaxLimit) {
					return new FieldValidationFailure(
						RuleType.Number,
						'Your total amount must be below ${{maxPaymentAmount}}'
					)
				}
				return fieldValidationSuccess;
			}

			if (amount === 0) {
				return new FieldValidationFailure(RuleType.Number, `Please enter an amount`);
			}

			if (isNaN(amount) || amount < minPaymentAmount || amount > maxPaymentAmount) {
				return new FieldValidationFailure(
					RuleType.Number,
					'Your amount must be between ${{minPaymentAmount}} and ${{maxPaymentAmount}}'
				);
			}

			return fieldValidationSuccess;
		}, RuleType.Number);
	};

	private createOneTimeGiftDateValidator = () =>
		createValidatorFunc<OptionalDate>((giftDate) => {
			if (this.giftType.value === GiftType.Recurring) {
				return fieldValidationSuccess;
			}

			if (!giftDate) {
				return new FieldValidationFailure(RuleType.Required, 'Please provide a date');
			}

			const today = moment();
			const isBeforeToday = today.isAfter(giftDate, 'day');
			if (isBeforeToday) {
				return new FieldValidationFailure(RuleType.Date, 'Date cannot be in the past');
			}

			return fieldValidationSuccess;
		}, RuleType.Required | RuleType.Date);

	private createStartDateValidator = () =>
		createValidatorFunc<OptionalDate>((startDate) => {
			if (this.giftType.value !== GiftType.Recurring) {
				return fieldValidationSuccess;
			}

			if (!startDate) {
				return new FieldValidationFailure(RuleType.Required, 'Please provide a starting date');
			}

			const today = moment();
			const isBeforeToday = today.isAfter(startDate, 'day');
			if (isBeforeToday) {
				return new FieldValidationFailure(RuleType.Date, 'Starting date cannot be in the past');
			}

			return fieldValidationSuccess;
		}, RuleType.Required | RuleType.Date);

	private createEndOccurrencesValidator = () =>
		createValidatorFunc<number | null>((endOccurrences) => {
			if (this.giftType.value !== GiftType.Recurring || this.endType.value !== EndType.Occurrences) {
				return fieldValidationSuccess;
			}

			if (endOccurrences === null || endOccurrences < 1 || endOccurrences > 1000) {
				return new FieldValidationFailure(RuleType.Required, 'Please provide a number between 1 and 1000');
			}

			var regex = new RegExp('^\\d+$');
			if (!regex.test(endOccurrences.toString())) {
				return new FieldValidationFailure(RuleType.Regex, 'Only whole numbers are accepted');
			}

			return fieldValidationSuccess;
		}, RuleType.Required);

	private createEndDateValidator = () =>
		createValidatorFunc<OptionalDate>((endDate) => {
			if (this.giftType.value !== GiftType.Recurring || this.endType.value !== EndType.Date) {
				return fieldValidationSuccess;
			}

			if (!endDate) {
				return new FieldValidationFailure(RuleType.Required, 'Please provide an end date');
			}

			const minDate = moment(this.minEndDate);
			const maxDate = moment(this.maxDate);
			const isBeforeMinDate = minDate.isAfter(endDate, 'day');
			const isAfterMaxDate = maxDate.isBefore(endDate, 'day');

			if (isBeforeMinDate) {
				return new FieldValidationFailure(RuleType.Date, 'End date must be 1 day after start date or later');
			}

			if (isAfterMaxDate) {
				return new FieldValidationFailure(RuleType.Date, 'End date must be before January 1, 2100');
			}

			return fieldValidationSuccess;
		}, RuleType.Required | RuleType.Date);

	@action
	private setStartDateToNextFirstOrFifteenth() {
		if (this.startDate.value) {
			const startDateDayOfMonth = moment(this.startDate.value).date();
			if (startDateDayOfMonth === 1 || startDateDayOfMonth === 15) {
				return;
			}
		}
		this.startDate.value = getNextFirstOrFifteenthAfter(moment()).toDate();
		this.handleStartDateChange(this.startDate.value);
	}

	private clearEndDateIfItIsBeforeStartDate(startDate: OptionalDate) {
		if (!(this.endDate.value instanceof Date)) {
			return;
		}

		if (!(startDate instanceof Date)) {
			return;
		}

		const endMoment = moment(this.endDate.value);
		const startMoment = moment(startDate);
		if (startMoment.isSameOrAfter(endMoment)) {
			this.endDate.value = null;
		}
	}
}
